import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errMessage } from "../../lib/errMessage";
import { trackEvent } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "btk_dsc_license_v1";

/**
 * <DSCLicenseGate utility="strategy-engine" purchaseUrl="/possibility-lattice">
 *   <YourGatedUtility />
 * </DSCLicenseGate>
 *
 * Renders a Zylvie license-key entry screen. Once a key is verified server-
 * side, it's cached in localStorage AND re-checked silently on every mount
 * (so refunded / cache-invalidated keys can be evicted later). Children only
 * render when the gate is unlocked.
 */
export default function DSCLicenseGate({
  utility,
  purchaseUrl,
  tierLabel,
  children,
}) {
  const [status, setStatus] = useState("loading"); // loading | locked | unlocked
  const [key, setKey] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // On mount: try to re-validate any cached key silently. If invalid, drop it.
  useEffect(() => {
    let cancelled = false;
    const cached = readCachedKey();
    if (!cached) {
      setStatus("locked");
      return undefined;
    }
    (async () => {
      try {
        const { data } = await axios.post(`${API}/dsc-license/check`, {
          license_key: cached,
          utility,
        });
        if (cancelled) return;
        if (data?.valid) {
          setStatus("unlocked");
        } else {
          clearCachedKey();
          setStatus("locked");
        }
      } catch (err) {
        if (cancelled) return;
        // Network error — fail open to cached key so the user isn't locked
        // out by a transient backend hiccup. The next page load re-checks.
        console.warn("DSC license re-check failed; using cached key", err);
        setStatus("unlocked");
      }
    })();
    return () => { cancelled = true; };
  }, [utility]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!key.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/dsc-license/check`, {
        license_key: key.trim(),
        utility,
      });
      if (data?.valid) {
        writeCachedKey(key.trim());
        trackEvent("dsc_license_unlock", { metadata: { utility } });
        setStatus("unlocked");
        const remaining = data?.runs_remaining;
        if (typeof remaining === "number") {
          toast.success(`Unlocked. ${remaining} run${remaining === 1 ? "" : "s"} remaining.`);
        } else {
          toast.success("Unlocked. Welcome in.");
        }
      } else if (data?.reason === "out_of_credits" && data?.purchase_url) {
        toast.error("This credit pack is empty. Buy more to keep using the tool.");
        setTimeout(() => { window.location.href = data.purchase_url; }, 1500);
      } else if (data?.reason === "utility_not_entitled" && data?.purchase_url) {
        toast.error("This license unlocks a different tool. Redirecting…");
        setTimeout(() => { window.location.href = data.purchase_url; }, 1500);
      } else {
        toast.error(data?.error || "That key isn't recognized. Double-check the welcome email.");
      }
    } catch (err) {
      toast.error(errMessage(err, "Could not verify license. Try again in a moment."));
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-900 text-white">
        <div className="text-sm text-white/55 uppercase tracking-[0.2em]">Checking access…</div>
      </div>
    );
  }

  if (status === "unlocked") {
    return children;
  }

  return (
    <div
      data-testid={`dsc-license-gate-${utility}`}
      className="min-h-screen flex items-center justify-center px-6 py-20 text-white"
      style={{ background: "radial-gradient(ellipse at top, rgba(212,175,55,0.10), transparent 60%), linear-gradient(180deg, #02050b 0%, #050a14 100%)" }}
    >
      <div className="max-w-md w-full rounded-2xl border border-gold/30 bg-black/60 backdrop-blur-md p-8 md:p-10" style={{ boxShadow: "0 0 60px rgba(212,175,55,0.20)" }}>
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold/85 mb-3">
          Licensed Access
        </div>
        <h1 className="font-display text-2xl md:text-3xl text-white leading-tight mb-3">
          Enter your license key
        </h1>
        <p className="text-sm text-white/65 leading-relaxed mb-6">
          {tierLabel ? `This unlocks ${tierLabel}. ` : ""}
          Paste the key from your Zylvie welcome email.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <input
            type="text"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            autoFocus
            data-testid="dsc-license-input"
            className="w-full px-4 py-3 rounded-lg bg-black/60 border border-white/15 focus:border-gold/60 focus:outline-none text-white placeholder:text-white/30 text-sm tracking-wider"
          />
          <button
            type="submit"
            disabled={submitting || !key.trim()}
            data-testid="dsc-license-submit"
            className="w-full px-5 py-3.5 rounded-full text-[12px] uppercase tracking-[0.24em] font-bold text-black disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: "linear-gradient(135deg, #F9E5C0 0%, #D4AF37 55%, #997A15 100%)", boxShadow: "0 0 28px rgba(212,175,55,0.30)" }}
          >
            {submitting ? "Verifying…" : "Unlock →"}
          </button>
        </form>
        <div className="mt-7 pt-6 border-t border-white/10 text-center">
          <p className="text-xs text-white/55 mb-3">
            Don't have a key yet?
          </p>
          {purchaseUrl && (
            <Link
              to={purchaseUrl}
              data-testid="dsc-license-purchase-link"
              className="text-sm text-gold hover:text-gold/80 underline-offset-4 hover:underline"
            >
              See pricing & buy access →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function readCachedKey() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.key || null;
  } catch (e) {
    return null;
  }
}

function writeCachedKey(key) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ key, saved_at: new Date().toISOString() }),
    );
  } catch (e) {
    /* noop */
  }
}

function clearCachedKey() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* noop */
  }
}
